import { Alert, Box, CircularProgress, Paper } from '@mui/material'
import { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import { requestWithUserAuth } from '../api/auth'
import { fetchRegistrationByTableNumber } from '../api/snacks'
import { fetchUserVotingCategory } from '../api/voting'
import { setError } from '../state/snacks'
import { useThemeMode } from '../theme'
import { AuthorizationGate } from './components/Authorization'
import { RegistrationCard } from './components/snacks/RegistrationCard'
import { SnacksMenu } from './components/snacks/SnacksMenu'
import { SnacksPageHeader } from './components/snacks/SnacksPageHeader'

const makeStyles = (palette) => ({
  pageBox: { width: '100%', display: 'flex', flexDirection: 'column' },
  contentBox: { maxWidth: '700px', margin: '0 auto', padding: '0 8px', width: '100%', mt: 1 },
  loadingBox: { display: 'flex', justifyContent: 'center', py: 4 },
  spinner: { color: palette.headerColor },
  alertMb: { mb: 2 }
})

export const SnacksExchangeVoting = () => {
  const { tableNumber } = useParams()
  const { t } = useTranslation()
  const { palette } = useThemeMode()
  const dispatch = useDispatch()
  const { config } = useSelector((state) => state.config)
  const auth = useSelector((state) => state.auth)
  const { selectedRegistration, loading, error, ownRegistration } = useSelector((state) => state.snacks)
  const isOwn = selectedRegistration != null && ownRegistration?.table_number === selectedRegistration.table_number

  const votingStart = useSelector((state) => state.voting?.snacks_exchange?.votingStart)
  const votingNotStarted = votingStart != null && votingStart > 0

  const tableNum = parseInt(tableNumber, 10)
  const styles = makeStyles(palette)

  useEffect(() => {
    dispatch(setError(null))
    requestWithUserAuth(config, auth, (auth) => {
      fetchRegistrationByTableNumber(config, auth, tableNum)
      fetchUserVotingCategory(config, auth, 'snacks_exchange')
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tableNumber, auth?.authToken])

  return (
    <AuthorizationGate>
      <Box sx={styles.pageBox}>
        <SnacksMenu />

        <SnacksPageHeader backTo="/voting/snacks" />

        <Box sx={styles.contentBox}>
          {loading && !selectedRegistration && (
            <Box sx={styles.loadingBox}>
              <CircularProgress sx={styles.spinner} />
            </Box>
          )}

          {votingNotStarted && (
            <Alert severity="info" sx={styles.alertMb}>
              {t('snacks-voting-not-started')}
            </Alert>
          )}

          {error && (
            <Alert severity="error" sx={styles.alertMb}>
              {error === 'Table not found'
                ? t('snacks-table-not-found', { number: tableNumber })
                : t('snacks-table-error')}
            </Alert>
          )}

          {!error && selectedRegistration && selectedRegistration.status !== 'active' && (
            <Alert severity="warning" sx={styles.alertMb}>
              {t('snacks-table-not-active')}
            </Alert>
          )}

          {!error && selectedRegistration && selectedRegistration.status === 'active' && (
            <Paper elevation={0} sx={{ p: 2, border: `1px solid ${palette.paperColorDivider ?? 'rgba(0,0,0,0.12)'}` }}>
              <RegistrationCard registration={selectedRegistration} isAdmin={false} isOwn={isOwn} showVoting flat compact singleRowVoting votingNotStarted={votingNotStarted} />
            </Paper>
          )}
        </Box>
      </Box>
    </AuthorizationGate>
  )
}
